import BN from 'bn.js'
import Web3 from 'web3'
import moment from 'moment'
import { ERC20_ABI } from '../abi/ERC20_ABI'
import { Pool_ABI } from '../abi/Pool_ABI'
import WalletState, { MAX_INT, ZERO_ADDRESS } from '../state/WalletState'
import { showFromWei, toWei } from '../utils/utils'

const READ_RPC_URL = 'https://bsc.nodereal.io'

function getProvider() {
  return WalletState.getProvider()
}

function getReadWeb3() {
  if (READ_RPC_URL) {
    return new Web3(new Web3.providers.HttpProvider(READ_RPC_URL))
  }
  const provider = getProvider()
  if (!provider) {
    throw new Error('No read provider available')
  }
  return new Web3(provider)
}

function getWeb3() {
  const provider = getProvider()
  if (!provider) {
    throw new Error('\u672a\u68c0\u6d4b\u5230\u94b1\u5305\uff0c\u8bf7\u5148\u5b89\u88c5 OKX Wallet \u6216 MetaMask\u3002')
  }
  Web3.givenProvider = provider
  return new Web3(provider)
}

function getPoolContract(web3) {
  return new web3.eth.Contract(Pool_ABI, WalletState.config.Pool)
}

function getTokenContract(web3, address) {
  return new web3.eth.Contract(ERC20_ABI, address)
}

function safeSub(a, b) {
  return a.gte(b) ? a.sub(b) : new BN(0)
}

function isExpectedReadError(error) {
  const message = String((error && error.message) || error || '').toLowerCase()
  return (
    message.includes('execution reverted') ||
    message.includes('ds-math-sub-underflow') ||
    message.includes('call revert exception')
  )
}

async function withGasPrice(web3) {
  return new BN(await web3.eth.getGasPrice(), 10)
}

async function ensureApproval({ web3, tokenAddress, allowance, amount, account }) {
  if (!allowance || !amount || !allowance.lt(amount)) {
    return
  }
  const tokenContract = getTokenContract(web3, tokenAddress)
  let estimateGas = await tokenContract.methods.approve(WalletState.config.Pool, MAX_INT).estimateGas({ from: account })
  estimateGas = new BN(estimateGas, 10).mul(new BN(120)).div(new BN(100))
  const gasPrice = await withGasPrice(web3)
  const transaction = await tokenContract.methods.approve(WalletState.config.Pool, MAX_INT).send({
    from: account,
    gas: estimateGas,
    gasPrice,
  })
  if (!transaction.status) {
    throw new Error('approve fail')
  }
}

export function getInviteLink(account) {
  if (!account) {
    return ''
  }
  return `${window.location.origin}${window.location.pathname}?ref=${account}`
}

export function formatTimestamp(timestamp) {
  return moment(new BN(timestamp, 10).mul(new BN(1000)).toNumber()).format('YYYY-MM-DD HH:mm:ss')
}

export async function fetchMintVisibility() {
  const web3 = getReadWeb3()
  const poolContract = getPoolContract(web3)
  const mintInfoRet = await poolContract.methods.getMintInfo().call()
  return {
    pauseMint: String(mintInfoRet[6]).toLowerCase() === 'true',
  }
}

export async function fetchMintData(account) {
  const web3 = getWeb3()
  const poolContract = getPoolContract(web3)
  const mintInfoRet = await poolContract.methods.getMintInfo().call()

  const usdt = mintInfoRet[0]
  const usdtDecimals = parseInt(mintInfoRet[1], 10)
  const usdtSymbol = mintInfoRet[2]
  const mintPrice = new BN(mintInfoRet[3], 10)
  const mintQty = parseInt(mintInfoRet[4], 10)
  const mintedAmount = parseInt(mintInfoRet[5], 10)
  const pauseMint = mintInfoRet[6]
  const mintInviteRewardCondtion = parseInt(mintInfoRet[7], 10)
  const userLen = parseInt(mintInfoRet[8], 10)
  const accMintUsdt = new BN(mintInfoRet[9], 10)

  const configInfo = {
    pauseMint,
    usdt,
    usdtDecimals,
    usdtSymbol,
    mintPrice,
    showMintPrice: showFromWei(mintPrice, usdtDecimals, 2),
    accMintUsdt: showFromWei(accMintUsdt, usdtDecimals, 2),
    userLen,
    mintInviteRewardCondtion,
    mintQty,
    mintedAmount,
  }

  const userInfo = {
    usdtBalance: new BN(0),
    usdtAllowance: new BN(0),
    showUsdtBalance: '--',
    mintNum: 0,
    inviteNum: 0,
    inviteRewardNum: 0,
    inviteIndex: 0,
    debtNum: mintInviteRewardCondtion,
    isActive: false,
    invitor: '',
  }

  if (account) {
    try {
      const userInfoRet = await poolContract.methods.getUserInfo(account).call()
      const usdtBalance = new BN(userInfoRet[0], 10)
      const usdtAllowance = new BN(userInfoRet[1], 10)

      const userMintInfoRet = await poolContract.methods._mintInfo(account).call()
      const mintNum = parseInt(userMintInfoRet.mintNum, 10)
      const inviteNum = parseInt(userMintInfoRet.inviteNum, 10)
      const inviteRewardNum = parseInt(userMintInfoRet.inviteRewardNum, 10)
      const inviteIndex = parseInt(userMintInfoRet.inviteIndex, 10)

      let isActive = false
      let invitor = ''
      try {
        const userBurnInfo = await poolContract.methods.userInfo(account).call()
        isActive = !!userBurnInfo.isActive
      } catch (error) {
        if (!isExpectedReadError(error)) {
          throw error
        }
      }
      try {
        const userTeamInfo = await poolContract.methods.getUserTeamInfo(account).call()
        invitor = userTeamInfo[2]
      } catch (error) {
        if (!isExpectedReadError(error)) {
          throw error
        }
      }

      userInfo.usdtBalance = usdtBalance
      userInfo.usdtAllowance = usdtAllowance
      userInfo.showUsdtBalance = showFromWei(usdtBalance, usdtDecimals, 2)
      userInfo.mintNum = mintNum
      userInfo.inviteNum = inviteNum
      userInfo.inviteRewardNum = inviteRewardNum
      userInfo.inviteIndex = inviteIndex
      userInfo.debtNum = Math.max(mintInviteRewardCondtion - inviteIndex, 0)
      userInfo.isActive = isActive
      userInfo.invitor = invitor
    } catch (error) {
      if (!isExpectedReadError(error)) {
        throw error
      }
    }
  }

  return { configInfo, userInfo }
}

export async function mintAction({ account, configInfo, userInfo, invitor }) {
  const web3 = getWeb3()
  const poolContract = getPoolContract(web3)
  const mintInvitor = invitor || ZERO_ADDRESS
  await ensureApproval({
    web3,
    tokenAddress: configInfo.usdt,
    allowance: userInfo.usdtAllowance,
    amount: configInfo.mintPrice,
    account,
  })

  let estimateGas = await poolContract.methods.mint(mintInvitor).estimateGas({ from: account })
  estimateGas = new BN(estimateGas, 10).mul(new BN(120)).div(new BN(100))
  const gasPrice = await withGasPrice(web3)

  return poolContract.methods.mint(mintInvitor).send({
    from: account,
    gas: estimateGas,
    gasPrice,
  })
}

export async function fetchBurnData(account) {
  const web3 = getWeb3()
  const poolContract = getPoolContract(web3)
  const burnInfoRet = await poolContract.methods.getBurnInfo().call()

  const token = burnInfoRet[0]
  const tokenSymbol = burnInfoRet[1]
  const tokenDecimals = parseInt(burnInfoRet[2], 10)
  const tokenPrice = new BN(burnInfoRet[3], 10)
  const poolBalance = new BN(burnInfoRet[4], 10)
  const minAmount = new BN(burnInfoRet[5], 10)
  const rewardRate = parseInt(burnInfoRet[6], 10)
  const todayInfo = burnInfoRet[7]
  const todayAccToken = new BN(todayInfo.accToken, 10)
  const todayAccAmount = new BN(todayInfo.accAmount, 10)
  const todayAccReward = new BN(todayInfo.accReward, 10)
  const todayReturnReward = new BN(todayInfo.returnReward, 10)
  const todayReward = safeSub(todayAccReward, todayReturnReward)
  const userLen = parseInt(burnInfoRet[8], 10)

  const poolInfo = await poolContract.methods.poolInfo().call()
  const totalAmount = new BN(poolInfo.totalAmount, 10)
  const accReward = new BN(poolInfo.accReward, 10)
  const accAmount = new BN(poolInfo.accAmount, 10)
  const accClaimedAmount = new BN(poolInfo.accClaimedAmount, 10)
  const accToken = new BN(poolInfo.accToken, 10)
  const returnReward = new BN(poolInfo.returnReward, 10)
  const totalReward = safeSub(accReward, returnReward)

  const mintInfoRet = await poolContract.methods.getMintInfo().call()
  const usdtDecimals = parseInt(mintInfoRet[1], 10)
  const nowTime = parseInt(await poolContract.methods.getTime().call(), 10)

  const configInfo = {
    token,
    tokenDecimals,
    tokenSymbol,
    totalAmount: showFromWei(totalAmount, 18, 4),
    poolBalance: showFromWei(poolBalance, 18, 4),
    totalReward: showFromWei(totalReward, 18, 4),
    accClaimedAmount: showFromWei(accClaimedAmount, 18, 4),
    accAmount: showFromWei(accAmount, 18, 4),
    accToken: showFromWei(accToken, tokenDecimals, 2),
    rewardRate,
    showRewardRate: rewardRate / 10000,
    minAmount,
    showMinAmount: showFromWei(minAmount, 18, 4),
    tokenPrice: showFromWei(tokenPrice, usdtDecimals, 5),
    userLen,
    todayAccToken: showFromWei(todayAccToken, tokenDecimals, 2),
    todayAccAmount: showFromWei(todayAccAmount, 18, 4),
    todayReward: showFromWei(todayReward, 18, 4),
    showNowTime: formatTimestamp(nowTime),
  }

  const userInfo = {
    isActive: false,
    showTokenBalance: '--',
    tokenBalance: new BN(0),
    tokenAllowance: new BN(0),
    binderLen: 0,
    inviteTokenReward: '--',
    teamTokenReward: '--',
    cooperationReward: '--',
    amount: '--',
    level: 0,
    invitor: '',
    teamNum: 0,
    teamAmount: '--',
    teamToken: '--',
    inviteAmount: '--',
    inviteToken: '--',
    maxReward: '--',
    accClaimed: '--',
    remainReward: '--',
    userAccAmount: '--',
    userAccToken: '--',
    pendingReward: '0.0000',
  }

  if (account) {
    try {
      const userInfoRet = await poolContract.methods.getUserInfo(account).call()
      const tokenBalance = new BN(userInfoRet[2], 10)
      const tokenAllowance = new BN(userInfoRet[3], 10)
      const pendingReward = new BN(userInfoRet[5], 10)

      const userBurnInfo = await poolContract.methods.userInfo(account).call()
      const amount = new BN(userBurnInfo.amount, 10)
      const maxReward = new BN(userBurnInfo.maxReward, 10)
      const userAccAmount = new BN(userBurnInfo.accAmount, 10)
      const accClaimed = new BN(userBurnInfo.accClaimed, 10)
      const userAccToken = new BN(userBurnInfo.accToken, 10)
      const remainReward = safeSub(maxReward, accClaimed)

      let binderLen = 0
      let teamNum = 0
      let invitor = ''
      let inviteTokenRewardRaw = new BN(0)
      let teamTokenRewardRaw = new BN(0)
      let teamAmount = new BN(0)
      let level = 0
      let teamToken = new BN(0)
      let inviteAmount = new BN(0)
      let inviteToken = new BN(0)

      try {
        const userTeamInfo = await poolContract.methods.getUserTeamInfo(account).call()
        binderLen = parseInt(userTeamInfo[0], 10)
        teamNum = parseInt(userTeamInfo[1], 10)
        invitor = userTeamInfo[2]
        inviteTokenRewardRaw = new BN(userTeamInfo[3], 10)
        teamTokenRewardRaw = new BN(userTeamInfo[4], 10)
        teamAmount = new BN(userTeamInfo[5], 10)
        level = parseInt(userTeamInfo[6], 10)
        teamToken = new BN(userTeamInfo[7], 10)
        inviteAmount = new BN(userTeamInfo[8], 10)
        inviteToken = new BN(userTeamInfo[9], 10)
      } catch (error) {
        if (!isExpectedReadError(error)) {
          throw error
        }
      }

      const cooperationRewardRaw = inviteTokenRewardRaw.add(teamTokenRewardRaw)

      userInfo.isActive = !!userBurnInfo.isActive
      userInfo.showTokenBalance = showFromWei(tokenBalance, tokenDecimals, 2)
      userInfo.tokenBalance = tokenBalance
      userInfo.tokenAllowance = tokenAllowance
      userInfo.binderLen = binderLen
      userInfo.inviteTokenReward = showFromWei(inviteTokenRewardRaw, tokenDecimals, 2)
      userInfo.teamTokenReward = showFromWei(teamTokenRewardRaw, tokenDecimals, 2)
      userInfo.cooperationReward = showFromWei(cooperationRewardRaw, tokenDecimals, 2)
      userInfo.amount = showFromWei(amount, 18, 4)
      userInfo.level = level
      userInfo.invitor = invitor
      userInfo.teamNum = teamNum
      userInfo.teamAmount = showFromWei(teamAmount, 18, 4)
      userInfo.teamToken = showFromWei(teamToken, tokenDecimals, 2)
      userInfo.inviteAmount = showFromWei(inviteAmount, 18, 4)
      userInfo.inviteToken = showFromWei(inviteToken, tokenDecimals, 2)
      userInfo.maxReward = showFromWei(maxReward, 18, 4)
      userInfo.accClaimed = showFromWei(accClaimed, 18, 4)
      userInfo.remainReward = showFromWei(remainReward, 18, 4)
      userInfo.userAccAmount = showFromWei(userAccAmount, 18, 4)
      userInfo.userAccToken = showFromWei(userAccToken, tokenDecimals, 2)
      userInfo.pendingReward = showFromWei(pendingReward, 18, 4)
    } catch (error) {
      if (!isExpectedReadError(error)) {
        throw error
      }
    }
  }

  return { configInfo, userInfo }
}

export async function previewBurn({ amount, rewardRate, tokenDecimals }) {
  const burnAmount = toWei(amount, 18)
  if (!burnAmount || burnAmount.isZero()) {
    return {
      tokenOut: new BN(0),
      showTokenOut: '--',
      rewardOut: '--',
    }
  }

  const rewardOut = burnAmount.mul(new BN(rewardRate)).div(new BN(10000))
  const web3 = getWeb3()
  const poolContract = getPoolContract(web3)
  let tokenAmount = await poolContract.methods.getTokenAmount(burnAmount).call()
  tokenAmount = new BN(tokenAmount, 10).mul(new BN(105)).div(new BN(100))

  return {
    tokenOut: tokenAmount,
    showTokenOut: showFromWei(tokenAmount, tokenDecimals, 4),
    rewardOut: showFromWei(rewardOut, 18, 4),
  }
}

export async function burnAction({ account, inputAmount, configInfo, userInfo, invitor }) {
  const web3 = getWeb3()
  const poolContract = getPoolContract(web3)
  const burnAmount = toWei(inputAmount, 18)
  let tokenAmount = await poolContract.methods.getTokenAmount(burnAmount).call()
  tokenAmount = new BN(tokenAmount, 10).mul(new BN(105)).div(new BN(100))

  if (userInfo.tokenBalance.lt(tokenAmount)) {
    throw new Error(`${configInfo.tokenSymbol} 浣欓涓嶈冻`)
  }

  await ensureApproval({
    web3,
    tokenAddress: configInfo.token,
    allowance: userInfo.tokenAllowance,
    amount: tokenAmount,
    account,
  })

  let estimateGas = await poolContract.methods.burn(invitor || ZERO_ADDRESS, burnAmount).estimateGas({ from: account })
  estimateGas = new BN(estimateGas, 10).mul(new BN(120)).div(new BN(100))
  const gasPrice = await withGasPrice(web3)

  return poolContract.methods.burn(invitor || ZERO_ADDRESS, burnAmount).send({
    from: account,
    gas: estimateGas,
    gasPrice,
  })
}

export async function claimAction(account) {
  const web3 = getWeb3()
  const poolContract = getPoolContract(web3)
  let estimateGas = await poolContract.methods.claim().estimateGas({ from: account })
  estimateGas = new BN(estimateGas, 10).mul(new BN(120)).div(new BN(100))
  const gasPrice = await withGasPrice(web3)

  return poolContract.methods.claim().send({
    from: account,
    gas: estimateGas,
    gasPrice,
  })
}
